import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { WinPresentation } from '@Core/Win/WinPresentation';
import { STATIC_JACKPOT_STEPS_CONFIG } from '@Core/Win/WinPresentation.constants';
import { WinPresentationEventSubscriptionsBuilder } from '@Core/Win/WinPresentationEventSubscriptionsBuilder';
import { gameInstance } from '@Game/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IJackpotSymbol, IJackpotWin, IStaticJackpotFeature, ISubLevel } from '@GFTypes/GameStoreTypes';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EJackpotType } from '@Managers/Redux/HttpState';
import { EntryCondition } from '@Managers/StateMachine/EntryCondition';
import { LeaveCondition } from '@Managers/StateMachine/LeaveCondition';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import { GameScene } from '..';
import { isLastLockAndRollSpin } from './SpecialGamesHelper';

const entryCondition = new EntryCondition<EGameStates>('checkIfAnyStaticJackpotIsWon', () => {
    const { specialGames } = gameInstance.gameStore.get(EGameStoreKeys.SpinOutcome);
    // TODO: check if jackpot won is a static jackpot
    const isAnyJackpotWon = !!specialGames?.lockAndRollData?.symbols.some((symbol) => symbol.jackpot);
    if (isLastLockAndRollSpin(gameInstance.gameStore) && isAnyJackpotWon) return true;
    return false;
});

const staticJackpotWinLeaveCondition = new LeaveCondition<EGameStates>('waitForWinPresentationHidden');

const subStates = {
    [EGameStates.PRE_STATIC_JACKPOT_WIN_STATE]: {
        entryConditions: [entryCondition],
        leaveConditions: []
    },
    [EGameStates.STATIC_JACKPOT_WIN_STATE]: {
        entryConditions: [entryCondition],
        leaveConditions: [staticJackpotWinLeaveCondition]
    },
    [EGameStates.POST_STATIC_JACKPOT_WIN_STATE]: {
        entryConditions: [entryCondition],
        leaveConditions: []
    }
};

export class StaticJackpotWin extends GameComponent {
    static readonly SPINE_KEY: string = 'jackpotWins';
    static readonly SKIN_NAME: string = 'default';
    public static readonly ODOMETER_SLOT_NAME = 'jackpotwin_odometer_placeholder';

    private readonly spineWrapper: SpineWrapper;
    private readonly jackpotSymbols: Map<number, IJackpotSymbol>;
    private readonly jackpotWinConfig: IJackpotWin[];
    private subLevels: ISubLevel[];
    private win: number;

    constructor(scene: GameScene, winPresentation: WinPresentation, staticJackpot: IStaticJackpotFeature) {
        super(scene);
        this.jackpotSymbols = staticJackpot.symbolsById;
        this.spineWrapper = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.width / 2,
                y: this.scene.screen.height / 2
            },
            { dataKey: StaticJackpotWin.SPINE_KEY, skinName: StaticJackpotWin.SKIN_NAME },
            true
        );
        this.spineWrapper.spine.zIndex = ELayeringOrder.SPECIAL_GAMES;
        this.spineWrapper.spine.visible = false;
        StateManager.assignSubStates(EGameStates.PRE_WIN_FEATURES_STATE, subStates);
        this.jackpotWinConfig = ConfigLoader.getConfiguration(EConfigurationNames.GAME).jackpotWins;
        const eventSubscriptions = new WinPresentationEventSubscriptionsBuilder()
            .during(EGameStates.STATIC_JACKPOT_WIN_STATE)
            .on(EGameStates.STATIC_JACKPOT_WIN_STATE, () =>
                winPresentation.startWin(this.gameStore.get(EGameStoreKeys.LastSpinBet)!, this.win, {
                    winPresentationStepsConfig: STATIC_JACKPOT_STEPS_CONFIG,
                    spine: this.spineWrapper,
                    winPresentationSubLevels: this.subLevels,
                    odometerSlotName: StaticJackpotWin.ODOMETER_SLOT_NAME
                })
            )
            .on(EGameEvents.SKIP, winPresentation.skipWinPresentation, winPresentation)
            .on(EWinPresentationStates.POST_WIN_PRESENTATION_STATE, () => staticJackpotWinLeaveCondition.resolve())
            .build();
        winPresentation.assignEventSubscriptions(...eventSubscriptions);
    }

    onInit(): void {
        EventsCenter.on(EGameEvents.ACTION_RESPONSE_PROCESSED, this.onSpinResultReceived, this);
    }

    private onSpinResultReceived(): void {
        const { symbols } = this.gameStore.get(EGameStoreKeys.LockAndRoll);

        this.win = symbols.filter((symbol) => symbol.jackpot).reduce((sum, symbol) => sum + symbol.win, 0);

        let jackpotName: EJackpotType | undefined;
        let highestJackpotId = -1;

        for (const symbol of symbols) {
            const jackpotSymbol = this.jackpotSymbols.get(symbol.id);

            if (!jackpotSymbol) continue;

            if (jackpotSymbol.jackpotId > highestJackpotId) {
                highestJackpotId = jackpotSymbol.jackpotId;
                jackpotName = jackpotSymbol.jackpotName;
            }
        }

        if (!jackpotName) return;
        const subLevels = this.jackpotWinConfig.find((jackpotLevel) => jackpotLevel.name === jackpotName)?.subLevels;
        if (!subLevels) return;
        this.subLevels = subLevels;
    }
}
